import React, { useRef, useState } from 'react';

import { PROPOSAL_FILE_ACCEPT } from 'components/molecules/ai_management_proposal_requirement';
import type {
  ProposalManualRequirementPayload,
  ProposalRequirementMomento,
} from 'services/actions/ai_management_proposal_documents';
import {
  MOMENTO_LABEL,
  PROPOSAL_REQUIREMENT_MOMENTOS,
} from 'services/ai_management_proposal_documents';
import { Container } from './styles';

interface AiManagementProposalAddRequirementProps {
  defaultMomento: ProposalRequirementMomento;
  // Resolve com a mensagem de erro, ou null em sucesso.
  onSubmit: (
    payload: ProposalManualRequirementPayload,
    file: File | null,
  ) => Promise<string | null>;
  onCancel: () => void;
}

// Exigência do cliente, fora do edital: aqui só nome e momento (o texto da declaração é
// preenchido depois, no editor). Toda exigência daqui é de proposta.
const AiManagementProposalAddRequirement: React.FC<AiManagementProposalAddRequirementProps> = ({
  defaultMomento,
  onSubmit,
  onCancel,
}) => {
  const [tipoExigencia, setTipoExigencia] = useState<'declaracao' | 'documento'>('documento');
  const [nome, setNome] = useState('');
  const [momento, setMomento] = useState<ProposalRequirementMomento>(defaultMomento);
  const [tipo, setTipo] = useState<'arquivo' | 'amostra'>('arquivo');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const isDocumento = tipoExigencia === 'documento';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!nome.trim()) {
      setError('Informe o nome da exigência.');
      return;
    }
    setSaving(true);
    setError(null);
    const message = await onSubmit(
      {
        tipo_exigencia: tipoExigencia,
        nome: nome.trim(),
        momento,
        ...(isDocumento ? { tipo } : {}),
      },
      isDocumento && tipo === 'arquivo' ? file : null,
    );
    setSaving(false);
    if (message) setError(message);
  };

  return (
    <Container className="card mb-3">
      <form className="px-4 py-3" onSubmit={handleSubmit}>
        <p className="add-requirement-title mb-3">Adicionar exigência</p>

        <div className="form-group">
          <span className="d-block mb-1">Tipo</span>
          {(['documento', 'declaracao'] as const).map((value) => (
            <div key={value} className="custom-control custom-radio custom-control-inline">
              <input
                type="radio"
                id={`add-requirement-tipo-${value}`}
                className="custom-control-input"
                checked={tipoExigencia === value}
                onChange={() => setTipoExigencia(value)}
              />
              <label className="custom-control-label" htmlFor={`add-requirement-tipo-${value}`}>
                {value === 'documento' ? 'Documento' : 'Declaração'}
              </label>
            </div>
          ))}
        </div>

        <label className="form-group d-block" htmlFor="add-requirement-nome">
          <span className="d-block mb-1">Nome</span>
          <input
            id="add-requirement-nome"
            type="text"
            className="form-control"
            value={nome}
            maxLength={255}
            onChange={(event) => setNome(event.target.value)}
          />
        </label>

        <div className="form-row">
          <label className="form-group col-sm-12" htmlFor="add-requirement-momento">
            <span className="d-block mb-1">Momento</span>
            <select
              id="add-requirement-momento"
              className="form-control"
              value={momento}
              onChange={(event) => setMomento(event.target.value as ProposalRequirementMomento)}
            >
              {PROPOSAL_REQUIREMENT_MOMENTOS.map((value) => (
                <option key={value} value={value}>
                  {MOMENTO_LABEL[value]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {isDocumento && (
          <div className="form-group">
            <span className="d-block mb-1">Entrega</span>
            {(['arquivo', 'amostra'] as const).map((value) => (
              <div key={value} className="custom-control custom-radio custom-control-inline">
                <input
                  type="radio"
                  id={`add-requirement-entrega-${value}`}
                  className="custom-control-input"
                  checked={tipo === value}
                  onChange={() => setTipo(value)}
                />
                <label
                  className="custom-control-label"
                  htmlFor={`add-requirement-entrega-${value}`}
                >
                  {value === 'arquivo' ? 'Arquivo' : 'Amostra (entrega física)'}
                </label>
              </div>
            ))}
          </div>
        )}

        {isDocumento && tipo === 'arquivo' && (
          <div className="form-group">
            <input
              ref={fileInput}
              type="file"
              accept={PROPOSAL_FILE_ACCEPT}
              className="d-none"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              className="btn btn-outline-primary btn-sm mr-2"
              onClick={() => fileInput.current?.click()}
            >
              {file ? 'Trocar arquivo' : 'Escolher arquivo (opcional)'}
            </button>
            {file && <span className="text-muted">{file.name}</span>}
          </div>
        )}

        {error && <div className="alert alert-danger py-2">{error}</div>}

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-outline-secondary mr-2"
            disabled={saving}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Adicionando…' : 'Adicionar'}
          </button>
        </div>
      </form>
    </Container>
  );
};

export default AiManagementProposalAddRequirement;
